/**
 * Author:       Sean Johnson <sean@scj.net>
 * Purpose:      Launcher listing the sites and apps this user can open (from config/apps.ts)
 * Last updated: 2026-10-18
 */
import { visibleSections } from '../config/apps'

interface AppLauncherProps {
  groups: string[]
  isAdmin: boolean
}

export function AppLauncher({ groups, isAdmin }: AppLauncherProps) {
  const sections = visibleSections(groups, isAdmin)

  if (sections.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Connected Services</h2>
        <p className="text-sm text-gray-500">No apps are enabled for this account yet.</p>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Connected Services</h2>
      {sections.map((section) => (
        <div key={section.title}>
          <div className="flex items-baseline justify-between mb-1">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{section.title}</h3>
            <span className="text-xs text-gray-500">{section.note}</span>
          </div>
          <div>
            {section.apps.map((app, i, arr) => (
              <div
                key={app.url}
                className={`flex items-center justify-between py-2 ${i < arr.length - 1 ? 'border-b border-gray-200' : ''}`}
              >
                <div className="min-w-0 pr-3">
                  <div className="font-medium">{app.name}</div>
                  <div className="text-xs text-gray-500 truncate">{app.description}</div>
                </div>
                <a href={app.url} className="auth-link text-sm shrink-0" target="_blank" rel="noopener noreferrer">
                  Open →
                </a>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
